<template>
  <div>
    <div class="flex items-center space-x-4">
      <router-link :to="{ name: 'postsIndex' }" class="system-button">
        <arrow-icon class="w-4 rotate-90"></arrow-icon>
      </router-link>
      <h2>{{ isEditing ? "Editar" : "Nueva" }} novedad</h2>
    </div>

    <div v-if="!loading" class="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-2">
      <form class="lg:col-span-2 flex flex-col space-y-4" @submit.prevent="save">
        <div class="card">
          <div class="flex flex-col">
            <label for="title">Título</label>
            <input id="title" type="text" v-model="form.title" maxlength="200" @input="autoSlug" />
            <validation-errors :errors="errorFor('title')"></validation-errors>
          </div>

          <div class="flex flex-col mt-4">
            <label for="slug">URL</label>
            <div class="flex items-center space-x-2">
              <span class="text-sm text-content-300 whitespace-nowrap">/novedades/</span>
              <input id="slug" type="text" class="font-mono flex-1" v-model="form.slug" @input="slugTouched = true" />
            </div>
            <p class="text-xs text-content-300 mt-1">Se genera a partir del título; puedes ajustarla antes de publicar.</p>
            <validation-errors :errors="errorFor('slug')"></validation-errors>
          </div>

          <div class="flex flex-col mt-4">
            <label for="excerpt">Resumen</label>
            <textarea id="excerpt" class="h-20" v-model="form.excerpt" maxlength="500" placeholder="Dos o tres líneas que aparecen en la lista de novedades y al compartir en redes."></textarea>
            <validation-errors :errors="errorFor('excerpt')"></validation-errors>
          </div>
        </div>

        <div class="card">
          <label>Contenido</label>
          <post-editor v-model="form.body" class="mt-1" />
          <validation-errors :errors="errorFor('body')"></validation-errors>
        </div>

        <div class="card">
          <label>Productos relacionados</label>
          <p class="text-xs text-content-300 mb-2">Se muestran al final del artículo con enlace a la tienda. Solo productos publicados.</p>

          <div class="relative">
            <input
              type="text"
              v-model="productQuery"
              placeholder="Buscar producto por nombre o SKU"
              @input="searchProducts"
              @focus="searchProducts"
            />
            <ul
              v-if="productResults.length"
              class="absolute z-20 left-0 right-0 bg-white border border-content-100 rounded shadow-lg max-h-64 overflow-auto mt-1"
            >
              <li
                v-for="product in productResults"
                :key="product.id"
                class="flex items-center space-x-3 px-3 py-2 hover:bg-primary-100 cursor-pointer"
                @mousedown.prevent="addProduct(product)"
              >
                <img v-if="product.image" :src="product.image" class="w-8 h-8 rounded object-cover" alt="" />
                <span class="flex-1 text-sm">{{ product.name }}</span>
                <span class="text-xs text-content-300 font-mono">{{ product.sku }}</span>
              </li>
            </ul>
          </div>

          <ul v-if="selectedProducts.length" class="mt-3 space-y-2">
            <li
              v-for="(product, index) in selectedProducts"
              :key="product.id"
              class="flex items-center space-x-3 border border-content-100 rounded px-3 py-2"
            >
              <img v-if="product.image" :src="product.image" class="w-10 h-10 rounded object-cover" alt="" />
              <div class="flex-1">
                <p class="text-sm font-medium">{{ product.name }}</p>
                <p v-if="product.is_published === false" class="text-xs text-warning-500">No publicado: no se mostrará en el sitio.</p>
              </div>
              <button type="button" class="system-button" :disabled="index === 0" @click="moveProduct(index, -1)" title="Subir">↑</button>
              <button type="button" class="system-button" :disabled="index === selectedProducts.length - 1" @click="moveProduct(index, 1)" title="Bajar">↓</button>
              <button type="button" class="delete-button" @click="removeProduct(index)">Quitar</button>
            </li>
          </ul>
          <p v-else class="text-sm text-content-300 mt-3">Sin productos vinculados.</p>
          <validation-errors :errors="errorFor('product_ids')"></validation-errors>
        </div>

        <div class="card">
          <h3 class="mb-2">SEO (opcional)</h3>
          <div class="flex flex-col">
            <label for="meta_title">Título para buscadores</label>
            <input id="meta_title" type="text" v-model="form.meta_title" maxlength="160" :placeholder="form.title || 'Se usa el título si se deja vacío'" />
            <validation-errors :errors="errorFor('meta_title')"></validation-errors>
          </div>
          <div class="flex flex-col mt-4">
            <label for="meta_description">Descripción para buscadores</label>
            <textarea id="meta_description" class="h-16" v-model="form.meta_description" maxlength="320" :placeholder="form.excerpt || 'Se usa el resumen si se deja vacía'"></textarea>
            <validation-errors :errors="errorFor('meta_description')"></validation-errors>
          </div>
        </div>
      </form>

      <aside class="flex flex-col space-y-4">
        <div class="card">
          <h3 class="mb-2">Publicación</h3>
          <label class="flex items-center space-x-2 cursor-pointer">
            <input type="checkbox" v-model="form.is_published" />
            <span>Publicada en el sitio</span>
          </label>
          <div class="flex flex-col mt-4">
            <label for="published_at">Fecha de publicación</label>
            <input id="published_at" type="datetime-local" v-model="form.published_at" />
            <p class="text-xs text-content-300 mt-1">
              Vacía = al momento de publicar. Una fecha futura programa la novedad.
            </p>
            <validation-errors :errors="errorFor('published_at')"></validation-errors>
          </div>

          <div class="flex flex-col space-y-2 mt-6">
            <button class="primary-button mx-0 w-full" @click="save" :disabled="saving">
              {{ saving ? "Guardando…" : isEditing ? "Guardar cambios" : "Crear novedad" }}
            </button>
            <a
              v-if="isEditing && isLive"
              :href="publicUrl"
              target="_blank"
              rel="noopener"
              class="secondary-button text-center"
              >Ver en el sitio</a
            >
          </div>
        </div>

        <div class="card">
          <h3 class="mb-2">Portada</h3>
          <div class="w-full aspect-video bg-background-100 rounded-lg overflow-hidden flex items-center justify-center">
            <img v-if="coverPreview" :src="coverPreview" class="w-full h-full object-cover" alt="" />
            <span v-else class="text-sm text-content-300">Sin portada</span>
          </div>
          <input ref="coverInput" type="file" accept="image/*" class="hidden" @change="onCoverPicked" />
          <div class="flex flex-wrap gap-2 mt-2">
            <button type="button" class="secondary-button" @click="$refs.coverInput.click()" :disabled="savingCover">
              {{ coverPreview ? "Cambiar" : "Agregar" }}
            </button>
            <button
              v-if="newCover"
              type="button"
              class="delete-button"
              @click="newCover = null; coverPreview = post?.cover_image_url || null"
            >
              Descartar
            </button>
            <button
              v-else-if="post && post.cover_image_url"
              type="button"
              class="delete-button"
              @click="removeCover"
              :disabled="savingCover"
            >
              Quitar
            </button>
          </div>
          <p v-if="newCover && isEditing" class="text-xs text-content-300 mt-2">La portada se sube al guardar.</p>
          <div class="flex flex-col mt-4">
            <label for="cover_image_alt">Texto alternativo</label>
            <input id="cover_image_alt" type="text" v-model="form.cover_image_alt" maxlength="255" placeholder="Describe la imagen" />
          </div>
        </div>
      </aside>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import { websiteUrl } from "@/runtimeConfig";
import validationErrors from "@/mixins/validationErrors";
import { is422 } from "@/utils/response";
import PostEditor from "@/components/PostEditor.vue";

export default {
  name: "PostForm",

  components: { PostEditor },

  mixins: [validationErrors],

  data() {
    return {
      loading: false,
      saving: false,
      savingCover: false,
      post: null,
      slugTouched: false,
      form: {
        title: "",
        slug: "",
        excerpt: "",
        body: "",
        is_published: false,
        published_at: "",
        meta_title: "",
        meta_description: "",
        cover_image_alt: "",
      },
      selectedProducts: [],
      productQuery: "",
      productResults: [],
      productSearchTimer: null,
      newCover: null,
      coverPreview: null,
    };
  },

  computed: {
    isEditing() {
      return !!this.$route.params.id;
    },

    isLive() {
      return this.post && this.post.is_published && this.post.published_at && new Date(this.post.published_at) <= new Date();
    },

    publicUrl() {
      return `${websiteUrl}/novedades/${this.post?.slug || ""}`;
    },
  },

  created() {
    this.$store.commit("setSelectedTab", "posts");
    if (this.isEditing) {
      this.loadPost();
    }
  },

  methods: {
    slugify(text) {
      return (text || "")
        .toString()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 200);
    },

    autoSlug() {
      if (!this.slugTouched && !this.isEditing) {
        this.form.slug = this.slugify(this.form.title);
      }
    },

    toLocalInput(datetime) {
      if (!datetime) return "";
      const d = new Date(datetime);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    async loadPost() {
      this.loading = true;
      try {
        const post = await api.posts.getById(this.$route.params.id);
        this.post = post;
        this.form = {
          title: post.title || "",
          slug: post.slug || "",
          excerpt: post.excerpt || "",
          body: post.body || "",
          is_published: !!post.is_published,
          published_at: this.toLocalInput(post.published_at),
          meta_title: post.meta_title || "",
          meta_description: post.meta_description || "",
          cover_image_alt: post.cover_image_alt || "",
        };
        this.selectedProducts = post.products || [];
        this.coverPreview = post.cover_image_url || null;
        this.slugTouched = true;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.loading = false;
    },

    searchProducts() {
      clearTimeout(this.productSearchTimer);
      this.productSearchTimer = setTimeout(async () => {
        try {
          const results = await api.posts.productOptions(this.productQuery);
          const selectedIds = new Set(this.selectedProducts.map((p) => p.id));
          this.productResults = results.filter((p) => !selectedIds.has(p.id));
        } catch (error) {
          this.productResults = [];
        }
      }, 250);
    },

    addProduct(product) {
      if (this.selectedProducts.length >= 12) {
        this.$store.dispatch("errorSnack", "Máximo 12 productos por novedad.");
        return;
      }
      this.selectedProducts.push(product);
      this.productResults = [];
      this.productQuery = "";
    },

    removeProduct(index) {
      this.selectedProducts.splice(index, 1);
    },

    moveProduct(index, delta) {
      const target = index + delta;
      if (target < 0 || target >= this.selectedProducts.length) return;
      const items = [...this.selectedProducts];
      [items[index], items[target]] = [items[target], items[index]];
      this.selectedProducts = items;
    },

    onCoverPicked(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      this.newCover = file;
      this.coverPreview = URL.createObjectURL(file);
    },

    async removeCover() {
      if (!this.post) return;
      this.savingCover = true;
      try {
        const response = await api.posts.removeCover(this.post.id);
        this.post = response.post;
        this.coverPreview = null;
        this.$store.dispatch("successSnack", response.message);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.savingCover = false;
    },

    async uploadCover(postId) {
      if (!this.newCover) return;
      const formData = new FormData();
      formData.append("image", this.newCover);
      if (this.form.cover_image_alt) formData.append("alt_text", this.form.cover_image_alt);
      await api.posts.saveCover(postId, formData);
      this.newCover = null;
    },

    validateForm() {
      this.errors = {};
      if (!this.form.title.trim()) this.errors.title = ["Campo requerido"];
      const text = (this.form.body || "").replace(/<[^>]*>/g, "").trim();
      if (!text && !/<img/i.test(this.form.body || "")) this.errors.body = ["Escribe el contenido de la novedad"];
      return Object.keys(this.errors).length === 0;
    },

    payload() {
      return {
        ...this.form,
        slug: this.form.slug || null,
        excerpt: this.form.excerpt || null,
        published_at: this.form.published_at || null,
        meta_title: this.form.meta_title || null,
        meta_description: this.form.meta_description || null,
        cover_image_alt: this.form.cover_image_alt || null,
        product_ids: this.selectedProducts.map((p) => p.id),
      };
    },

    async save() {
      if (!this.validateForm()) {
        this.$store.dispatch("errorSnack", "Favor de llenar todos los campos correctamente.");
        return;
      }

      this.saving = true;
      try {
        const response = this.isEditing
          ? await api.posts.update(this.post.id, this.payload())
          : await api.posts.create(this.payload());

        const postId = response.post.id;
        try {
          await this.uploadCover(postId);
        } catch (coverError) {
          this.$store.dispatch("errorSnack", coverError?.response?.data?.message || "La novedad se guardó, pero la portada no se pudo subir.");
          this.saving = false;
          this.$router.push({ name: "postsEditForm", params: { id: postId } });
          return;
        }

        this.$store.dispatch("successSnack", response.message);
        this.$router.push({ name: "postsIndex" });
      } catch (error) {
        if (is422(error)) {
          this.errors = (error.response && error.response.data.errors) || {};
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.saving = false;
    },
  },
};
</script>
